/**
 * Layout 1: Field Guide
 * Editorial/Magazine aesthetic inspired by premium travel publications
 * - Dramatic serif typography with oversized section numbers
 * - Asymmetric magazine-spread layouts
 * - Refined color palette with earth tones
 * - Subtle paper grain texture
 */
import { useCallback, useMemo, useRef, useState } from "react";
import { NavigationControl } from "react-map-gl/mapbox";
import type { Map as MapboxMap } from "mapbox-gl";
import {
  type PublishedLandingPage,
  type PlaceFeature,
  type PlaceGeoJSON,
  PLACE_TYPE_META,
  PlaceType,
  getPlaceTrack,
} from "../../lib/api";
import { FIT_BOUNDS_PADDING, FLY_TO_OPTIONS, FLY_TO_ZOOM } from "../../lib/mapConstants";
import { routes } from "../../lib/routes";
import { useShare } from "../../hooks/useShare";
import { NomaMap, AdventureMapPlacesLayer, SelectedPlaceLayer, RouteTrackLayer } from "../../components/map";

interface FieldGuideLandingProps {
  mapData: PublishedLandingPage;
  places: PlaceGeoJSON;
}

// Paper grain texture as data URI
const paperGrain = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

// Rich earth-tone palette per category
const typeStyles: Partial<Record<PlaceType, { accent: string; light: string; number: string }>> = {
  restaurant: { accent: "#8b4513", light: "#faf6f1", number: "#d4a574" },
  cafe: { accent: "#5d4e37", light: "#f8f6f2", number: "#a89078" },
  "grocery-store": { accent: "#4a5d3a", light: "#f4f7f2", number: "#8fa878" },
  hiking: { accent: "#2d5a48", light: "#f2f7f5", number: "#6b9a88" },
  accommodation: { accent: "#3d4f6a", light: "#f3f5f8", number: "#7a8fa8" },
};
const defaultStyle = { accent: "#666666", light: "#f5f5f5", number: "#999999" };
const getTypeStyle = (type: PlaceType) => typeStyles[type] ?? defaultStyle;

export default function FieldGuideLanding({ mapData, places }: FieldGuideLandingProps) {
  const share = useShare();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<PlaceFeature | null>(null);
  const [hoveredPlace, setHoveredPlace] = useState<string | null>(null);

  const placesByType = useMemo(() => {
    const grouped = new Map<PlaceType, PlaceFeature[]>();
    places.features.forEach((place) => {
      const type = place.properties.type;
      if (!grouped.has(type)) grouped.set(type, []);
      grouped.get(type)!.push(place);
    });
    return grouped;
  }, [places.features]);

  const handleMapLoad = useCallback((map: MapboxMap) => {
    mapRef.current = map;
  }, []);

  const handlePlaceClick = useCallback((feature: PlaceFeature) => {
    setSelectedPlace(feature);

    mapRef.current?.flyTo({
      ...FLY_TO_OPTIONS,
      center: feature.geometry.coordinates as [number, number],
      zoom: FLY_TO_ZOOM,
    });

    // Scroll to map
    if (mapContainerRef.current) {
      const top = mapContainerRef.current.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  }, []);

  const handleShare = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (mapData.slug) {
        share({
          url: routes.to.landingPages.bySlug(mapData.slug),
          title: mapData.name,
        });
      }
    },
    [share, mapData.slug, mapData.name],
  );

  return (
    <div
      className="min-h-screen"
      style={{
        fontFamily: "'DM Sans', 'Inter', sans-serif",
        background: "#faf8f4",
      }}
    >
      {/* Paper grain overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-50"
        style={{
          backgroundImage: paperGrain,
          opacity: 0.03,
          mixBlendMode: "multiply",
        }}
      />

      {/* Hero Section - Magazine Cover Style */}
      <header className="relative min-h-screen flex flex-col">
        {/* Top bar */}
        <div className="relative z-10 px-8 md:px-16 py-8 md:py-10 flex justify-between items-center">
          <div
            className="text-xs tracking-[0.4em] uppercase"
            style={{
              color: "#8b7355",
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontWeight: 600,
              letterSpacing: "0.35em",
            }}
          >
            A Field Guide
          </div>
          <button
            onClick={handleShare}
            className="group flex items-center gap-3 text-xs tracking-[0.2em] uppercase transition-colors hover:opacity-70"
            style={{ color: "#5a5346" }}
          >
            <span>Share</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
            </svg>
          </button>
        </div>

        {/* Main hero content */}
        <div className="flex-1 grid lg:grid-cols-2 gap-0">
          {/* Left: Title */}
          <div className="flex flex-col justify-center px-8 md:px-16 py-12 lg:py-0">
            <div className="max-w-xl">
              {/* Issue number style */}
              <div className="flex items-baseline gap-4 mb-8">
                <span
                  className="text-8xl md:text-9xl font-light leading-none"
                  style={{
                    color: "#e8e2d9",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                  }}
                >
                  {String(places.features.length).padStart(2, "0")}
                </span>
                <span className="text-sm tracking-[0.2em] uppercase" style={{ color: "#8b7355" }}>
                  Places
                </span>
              </div>

              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-normal leading-[0.95] mb-8"
                style={{
                  color: "#2a2520",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontWeight: 400,
                }}
              >
                {mapData.name}
              </h1>

              {mapData.description && (
                <p className="text-lg leading-relaxed max-w-md" style={{ color: "#5a5346" }}>
                  {mapData.description}
                </p>
              )}

              {/* Scroll indicator */}
              <button
                onClick={() => {
                  const main = document.querySelector("main");
                  if (main) {
                    const top = main.getBoundingClientRect().top + window.scrollY - 40;
                    window.scrollTo({ top, behavior: "smooth" });
                  }
                }}
                className="mt-16 flex items-center gap-4 group cursor-pointer"
              >
                <div className="w-px h-12 transition-all group-hover:h-16" style={{ background: "#d4cfc5" }} />
                <span
                  className="text-xs tracking-[0.2em] uppercase transition-colors group-hover:text-[#5a5346]"
                  style={{ color: "#8b7355" }}
                >
                  Explore Below
                </span>
              </button>
            </div>
          </div>

          {/* Right: Map */}
          <div ref={mapContainerRef} className="relative h-[50vh] lg:h-full lg:min-h-[600px]">
            <div className="h-full w-full" style={{ boxShadow: "-20px 0 60px rgba(0,0,0,0.08)" }}>
              <NomaMap bounds={places} padding={FIT_BOUNDS_PADDING.desktop} onLoad={handleMapLoad}>
                <NavigationControl position="bottom-right" />
                <AdventureMapPlacesLayer places={places} onClick={handlePlaceClick} />
                <RouteTrackLayer track={selectedPlace ? getPlaceTrack(selectedPlace.properties) : undefined} />
                <SelectedPlaceLayer place={selectedPlace} />
              </NomaMap>
            </div>

            {/* Selected place overlay */}
            {selectedPlace && (
              <div className="absolute bottom-12 left-4 right-4 lg:left-8 lg:right-16 z-10 animate-in slide-in-from-bottom-4 duration-300">
                <div
                  className="p-6"
                  style={{
                    background: "#faf8f4",
                    borderLeft: `3px solid ${getTypeStyle(selectedPlace.properties.type).accent}`,
                    boxShadow: "0 4px 24px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
                  }}
                >
                  <button
                    onClick={() => setSelectedPlace(null)}
                    className="absolute top-4 right-4 p-2 opacity-50 hover:opacity-100 transition-opacity"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="2">
                      <path d="M3 3l10 10M13 3L3 13" />
                    </svg>
                  </button>
                  <p
                    className="text-xs tracking-[0.25em] uppercase mb-2"
                    style={{ color: getTypeStyle(selectedPlace.properties.type).accent }}
                  >
                    {PLACE_TYPE_META[selectedPlace.properties.type].label}
                  </p>
                  <h3
                    className="text-2xl mb-3 pr-8"
                    style={{
                      color: "#2a2520",
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                    }}
                  >
                    {selectedPlace.properties.name}
                  </h3>
                  {selectedPlace.properties.description && (
                    <p className="text-sm leading-relaxed italic" style={{ color: "#5a5346" }}>
                      &ldquo;{selectedPlace.properties.description}&rdquo;
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content - Magazine Spreads */}
      <main className="relative">
        {places.features.length === 0 ? (
          <div className="py-32 text-center">
            <p
              className="text-2xl italic"
              style={{
                color: "#a09080",
                fontFamily: "'Cormorant Garamond', Georgia, serif",
              }}
            >
              This guide is being curated...
            </p>
          </div>
        ) : (
          <div>
            {Array.from(placesByType.entries()).map(([type, typePlaces], idx) => {
              const styles = getTypeStyle(type);
              const isEven = idx % 2 === 0;

              return (
                <section key={type} className="relative" style={{ background: isEven ? "#faf8f4" : styles.light }}>
                  {/* Section divider line */}
                  <div
                    className="absolute top-0 left-8 right-8 md:left-16 md:right-16 h-px"
                    style={{ background: "#e8e2d9" }}
                  />

                  <div className="px-8 md:px-16 py-20 md:py-32">
                    {/* Section header - Editorial style */}
                    <div className="mb-16">
                      <div className="flex items-baseline gap-4 md:gap-6">
                        <span
                          className="text-6xl md:text-8xl lg:text-9xl font-light leading-none"
                          style={{
                            color: styles.number,
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                          }}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <h2
                          className="text-3xl md:text-4xl lg:text-5xl font-normal"
                          style={{
                            color: "#2a2520",
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                          }}
                        >
                          {PLACE_TYPE_META[type].labelPlural}
                        </h2>
                      </div>
                      <div className="flex items-center gap-4 md:gap-6 mt-4">
                        {/* Spacer matching number width */}
                        <span
                          className="text-6xl md:text-8xl lg:text-9xl font-light leading-none invisible h-0"
                          aria-hidden="true"
                          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                        >
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <div className="w-24 h-px" style={{ background: styles.accent }} />
                      </div>
                    </div>

                    {/* Places - Asymmetric editorial grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                      {typePlaces.map((place, placeIdx) => {
                        const isFeature = placeIdx === 0 && typePlaces.length > 2;

                        return (
                          <button
                            key={place.id}
                            onClick={() => handlePlaceClick(place)}
                            onMouseEnter={() => setHoveredPlace(place.id as string)}
                            onMouseLeave={() => setHoveredPlace(null)}
                            className={`text-left group relative ${isFeature ? "md:col-span-2 lg:col-span-2" : ""}`}
                          >
                            {/* Vertical accent line */}
                            <div
                              className="absolute left-0 top-0 bottom-0 w-px transition-all duration-300"
                              style={{
                                background:
                                  hoveredPlace === place.id || selectedPlace?.id === place.id
                                    ? styles.accent
                                    : "#e8e2d9",
                                transform: hoveredPlace === place.id ? "scaleY(1)" : "scaleY(0.3)",
                                transformOrigin: "top",
                              }}
                            />

                            <div className="pl-6 py-2">
                              <h3
                                className={`${
                                  isFeature ? "text-3xl md:text-4xl" : "text-2xl"
                                } font-normal mb-3 transition-all duration-300`}
                                style={{
                                  color: hoveredPlace === place.id ? styles.accent : "#2a2520",
                                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                                  transform: hoveredPlace === place.id ? "translateX(8px)" : "translateX(0)",
                                }}
                              >
                                {place.properties.name}
                              </h3>

                              {place.properties.description && (
                                <p
                                  className={`leading-relaxed ${isFeature ? "text-base max-w-xl" : "text-sm"}`}
                                  style={{ color: "#6a6256" }}
                                >
                                  {place.properties.description}
                                </p>
                              )}

                              {/* Hover indicator */}
                              <div
                                className="mt-4 flex items-center gap-2 transition-all duration-300"
                                style={{
                                  color: styles.accent,
                                  opacity: hoveredPlace === place.id ? 1 : 0,
                                  transform: hoveredPlace === place.id ? "translateX(0)" : "translateX(-8px)",
                                }}
                              >
                                <span className="text-xs tracking-[0.15em] uppercase">View on map</span>
                                <svg
                                  width="14"
                                  height="14"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                >
                                  <path d="M3 8h10M10 5l3 3-3 3" />
                                </svg>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer - Refined */}
      <footer className="relative py-20 px-8 md:px-16" style={{ background: "#2a2520" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-end">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase mb-4" style={{ color: "rgba(255,255,255,0.6)" }}>
                Curated with care by {mapData.curatedBy?.displayName ?? "your local adventurers"}
              </p>
              <a
                href={routes.to.home()}
                className="text-4xl md:text-5xl font-light hover:opacity-70 transition-opacity"
                style={{
                  color: "#faf8f4",
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                }}
              >
                noma.to
              </a>
            </div>
            <div className="md:text-right">
              <a
                href={routes.to.home()}
                className="inline-block px-8 py-4 text-sm tracking-[0.2em] uppercase transition-all hover:translate-x-1"
                style={{
                  background: "#faf8f4",
                  color: "#2a2520",
                }}
              >
                Create Your Own Guide
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
